/**
 * @file useConversaoUM.ts
 * @summary Hook para conversão de unidades de medida do Protheus
 *
 * @remarks
 * - Suporta conversões automáticas via convert-units
 * - Inclui conversões customizadas para unidades comerciais
 * - Mapeia todas as UMs do Protheus
 *
 * @example
 * ```ts
 * import { useConversaoUM } from "@nucleo/hooks";
 *
 * const { converter } = useConversaoUM();
 *
 * // Conversão automática
 * const resultado = converter(10, "GL", "L "); // 10 galões → litros
 * console.log(resultado); // { quantidadeConvertida: 37.85, fatorConversao: 3.785, ... }
 *
 * // Conversão customizada
 * const resultado2 = converter(2, "DZ", "UNID"); // 2 dúzias → 24 unidades
 * console.log(resultado2); // { quantidadeConvertida: 24, fatorConversao: 12, ... }
 *
 * // Sem conversão (retorna original)
 * const resultado3 = converter(5, "AMPOLA", "LITRO"); // 5 ampolas → 5 ampolas
 * console.log(resultado3); // { quantidadeConvertida: 5, fatorConversao: 1, conversaoPossivel: false, ... }
 * ```
 */

"use client";

import { useCallback } from "react";

// =============================================
// Tipos
// =============================================

export interface ConversaoResult {
  quantidadeConvertida: number;
  fatorConversao: number;
  conversaoPossivel: boolean;
  metodo: "convert-units" | "custom" | "nenhum";
  umOrigem: string;
  umDestino: string;
}

// =============================================
// Mapeamentos
// =============================================

// Mapeamento das UMs do Protheus para convert-units
const protheusToConvertUnits: Record<string, string> = {
  // Volume
  GL: "gal", // GALAO → gallon
  "L ": "l", // LITRO → liter
  ML: "ml", // MILILITRO → milliliter
  M3: "m3", // METRO CUBICO → cubic meter
  CC: "cm3", // CENTIMETRO CUBICO → cubic centimeter
  TB: "gal", // TAMBOR → gallon (aproximação, se for tambor de líquido)
  BD: "l", // BALDE → liter (aproximação)
  LATA: "l", // LATA → liter (aproximação)

  // Peso
  KG: "kg", // QUILOGRAMA → kilogram
  "G ": "g", // GRAMA → gram
  AR: "lb", // ARROBA → pound (arroba é ~15kg, convert-units não tem arroba. Usar lb como placeholder ou remover)
  TON: "t", // TONELADA → metric ton (t)
  K: "ct", // QUILATE → carat (ct)

  // Comprimento
  "M ": "m", // METRO → meter
  CM: "cm", // CENTIMETRO → centimeter
  DM: "dm", // DECIMETRO → decimeter
  FT: "ft", // PES → foot

  // Área
  M2: "m2", // METRO QUADRADO → square meter
  CM2: "cm2", // CENTIMETRO QUADRADO → square centimeter

  // Energia
  MWH: "MWh", // MEGAWATT HORA → megawatt-hour
};

// Conversões customizadas para unidades comerciais que não são diretamente suportadas por convert-units
const conversoesCustomizadas: Record<string, Record<string, number>> = {
  DZ: {
    // DUZIA
    UNID: 12, // 1 DUZIA = 12 UNIDADES
    PC: 12, // 1 DUZIA = 12 PEÇAS
  },
  CENTO: {
    // CENTO (renomeado de CT para CENTO)
    UNID: 100, // 1 CENTO = 100 UNIDADES
    PC: 100, // 1 CENTO = 100 PEÇAS
    DZ: 100 / 12, // 1 CENTO = 8.33 DUZIAS
  },
  PARES: {
    // PARES (renomeado de P para PARES)
    UNID: 2, // 1 PAR = 2 UNIDADES
    PC: 2, // 1 PAR = 2 PEÇAS
  },
  GZ: {
    // GROZA
    UNID: 144, // 1 GROZA = 144 UNIDADES (12 dúzias)
    DZ: 12, // 1 GROZA = 12 DUZIAS
  },
  MILHEI: {
    // MILHEIRO (renomeado de MH para MILHEI)
    UNID: 1000, // 1 MILHEIRO = 1000 UNIDADES
    PC: 1000, // 1 MILHEIRO = 1000 PEÇAS
    CENTO: 10, // 1 MILHEIRO = 10 CENTOS
  },
  CX: {
    // CAIXA (genérica, precisa de fator manual)
    // Ex: 1 CX = X UNID (precisa ser definido manualmente)
  },
  CX2: { UNID: 2, PC: 2 },
  CX3: { UNID: 3, PC: 3 },
  CX5: { UNID: 5, PC: 5 },
  CX10: { UNID: 10, PC: 10 },
  CX15: { UNID: 15, PC: 15 },
  CX20: { UNID: 20, PC: 20 },
  CX25: { UNID: 25, PC: 25 },
  CX50: { UNID: 50, PC: 50 },
  CX100: { UNID: 100, PC: 100 },
  UNID: {
    // UNIDADE
    PC: 1, // 1 UNIDADE = 1 PEÇA
  },
  PC: {
    // PEÇA
    UNID: 1, // 1 PEÇA = 1 UNIDADE
  },
  RESMA: {
    // RESMA
    FOLHA: 500, // 1 RESMA = 500 FOLHAS
  },
  // Unidades como AMPOLA, BANDEJ, BARRA, BISNAG, BLOCO, BOBINA, BOMB, CAPS, CART, CJ, DISP, EMBAL, FARDO, FRASCO, GF, JOGO, KIT, PACOTE, PALETE, POTE, ROLO, SACO, SACOLA, TANQUE, TUBO, VASIL, VIDRO
  // Não possuem fatores de conversão fixos e universais para outras unidades listadas.
  // São tratadas como unidades base onde 1 unidade do tipo é apenas 1.
};

// =============================================
// Função de Conversão
// =============================================

/**
 * Função para converter unidades de medida usando convert-units ou conversões customizadas
 * @param quantidade - Quantidade a ser convertida
 * @param umOrigem - Unidade de origem (código Protheus)
 * @param umDestino - Unidade de destino (código Protheus)
 * @returns Resultado da conversão
 */
function converterUM(
  quantidade: number,
  umOrigem: string,
  umDestino: string
): ConversaoResult {
  console.log("🔄 [converterUM] Tentando converter:", {
    quantidade,
    umOrigem,
    umDestino,
  });

  // 1. Tenta conversão via convert-units
  const umOrigemConvert = protheusToConvertUnits[umOrigem];
  const umDestinoConvert = protheusToConvertUnits[umDestino];

  if (umOrigemConvert && umDestinoConvert) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const convert = require("convert-units");
      const resultado = convert(quantidade)
        .from(umOrigemConvert)
        .to(umDestinoConvert);

      console.log("✅ [converterUM] Conversão via convert-units:", resultado);

      return {
        quantidadeConvertida: resultado,
        fatorConversao: resultado / quantidade,
        conversaoPossivel: true,
        metodo: "convert-units",
        umOrigem,
        umDestino,
      };
    } catch (error) {
      console.warn("⚠️ [converterUM] Erro na conversão convert-units:", error);
    }
  }

  // 2. Tenta conversão via customizada
  const customConversionFactor = conversoesCustomizadas[umOrigem]?.[umDestino];
  if (customConversionFactor !== undefined) {
    const quantidadeConvertida = quantidade * customConversionFactor;
    console.log(
      "✅ [converterUM] Conversão via customizada:",
      quantidadeConvertida
    );
    return {
      quantidadeConvertida,
      fatorConversao: customConversionFactor,
      conversaoPossivel: true,
      metodo: "custom",
      umOrigem,
      umDestino,
    };
  }

  // 3. Se nenhuma conversão for encontrada, retorna a quantidade original
  console.warn(
    `⚠️ [converterUM] Nenhuma conversão encontrada para ${umOrigem} para ${umDestino}. Retornando quantidade original.`
  );
  return {
    quantidadeConvertida: quantidade,
    fatorConversao: 1,
    conversaoPossivel: false,
    metodo: "nenhum",
    umOrigem,
    umDestino,
  };
}

// =============================================
// Hook Principal
// =============================================

/**
 * Hook para gestão de conversões de unidades de medida do Protheus
 * @returns Funções e estado para conversão de UMs
 *
 * @remarks
 * - Suporta conversões automáticas via convert-units
 * - Inclui conversões customizadas para unidades comerciais
 * - Mapeia todas as UMs do Protheus
 *
 * @example
 * ```ts
 * import { useConversaoUM } from "@nucleo/hooks";
 *
 * const { converter } = useConversaoUM();
 *
 * // Conversão automática
 * const resultado = converter(10, "GL", "L "); // 10 galões → litros
 * console.log(resultado); // { quantidadeConvertida: 37.85, fatorConversao: 3.785, ... }
 *
 * // Conversão customizada
 * const resultado2 = converter(2, "DZ", "UNID"); // 2 dúzias → 24 unidades
 * console.log(resultado2); // { quantidadeConvertida: 24, fatorConversao: 12, ... }
 *
 * // Sem conversão (retorna original)
 * const resultado3 = converter(5, "AMPOLA", "LITRO"); // 5 ampolas → 5 ampolas
 * console.log(resultado3); // { quantidadeConvertida: 5, fatorConversao: 1, conversaoPossivel: false, ... }
 * ```
 */
export function useConversaoUM() {
  const converter = useCallback(
    (quantidade: number, umOrigem: string, umDestino: string) => {
      return converterUM(quantidade, umOrigem, umDestino);
    },
    []
  );

  return {
    converter,
    protheusToConvertUnits,
    conversoesCustomizadas,
  };
}
